import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faStar,
  faMoneyBill,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { FaPaperPlane } from "react-icons/fa6";
import { useCart } from "../context/CartContext.js";
import CounsellingModal from "../pages/CounsellingModal.js";

const CollegeByLocation = ({ location, excludeSlug }) => {
  const router = useRouter();
  const { addToCart } = useCart();
  const [colleges, setColleges] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCollege, setSelectedCollege] = useState(null);

  useEffect(() => {
    if (!location) return;
    const fetchColleges = async () => {
      try {
        const baseURL = process.env.NEXT_PUBLIC_GHOST_API_URL;
        const apiKey = process.env.NEXT_PUBLIC_GHOST_API_KEY;
        // Fetch a large number to ensure all are included
        const res = await axios.get(
          `${baseURL}/ghost/api/v3/content/posts/?key=${apiKey}&filter=tag:college&limit=1000`
        );
        // Filter by location in custom_excerpt
        const filtered = res.data.posts.filter((post) => {
          if (post.slug === excludeSlug) return false;
          try {
            const excerpt = JSON.parse(post.custom_excerpt || "{}");
            return (
              excerpt.location &&
              excerpt.location.toLowerCase() === location.toLowerCase()
            );
          } catch {
            return false;
          }
        });
        setColleges(filtered);
      } catch (error) {
        setColleges([]);
      }
    };
    fetchColleges();
  }, [location, excludeSlug]);

  if (!location) return null;
  if (colleges.length === 0) return null;

  const handleStartApplication = (post) => {
    let customExcerpt = {};
    try {
      customExcerpt = JSON.parse(post.custom_excerpt || "{}");
    } catch (error) {
      customExcerpt = {};
    }
    setSelectedCollege({
      ...post,
      course: customExcerpt.courseOffered || [],
      location: customExcerpt.location || "Location Not Available",
    });
    setIsModalOpen(true);
  };

  const handleViewDetails = (slug) => {
    router.push(`/college/${slug}`);
  };

  return (
    <div className="mt-12 px-5 md:px-10 xl:px-24">
      <h2 className="text-xl font-bold mb-6">
      Other Colleges in {location}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {colleges.slice(0, 8).map((post) => {
          let customExcerpt = {};
          try {
            customExcerpt = JSON.parse(post.custom_excerpt);
          } catch (error) {
            customExcerpt = { text: post.custom_excerpt };
          }
          return (
            <div key={post.id} className="p-2">
              <div
                className="border rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow duration-300 w-full max-w-[350px] mx-auto flex-col h-full cursor-pointer"
                onClick={() => handleViewDetails(post.slug)}
              >
                <img
                  src={
                    post.feature_image?.replace("ghost.", "") ||
                    "/default-image.jpg"
                  }
                  alt={post.title}
                  layout="responsive"
                  width={350}
                  height={40}
                  className="rounded-lg w-full h-40 object-fill"
                />
                <div className="mt-4 flex-grow">
                  <h3 className="text-sm md:text-md font-semibold line-clamp-2 overflow-hidden text-ellipsis whitespace-normal">
                    {post.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500">
                    Course: {customExcerpt.courseOffered?.join(", ") || "N/A"}
                  </p>
                  <div className="mt-3 flex justify-between items-start">
                    <ul className="text-xs sm:text-sm text-gray-500 space-y-2">
                      <li className="flex items-center">
                        <FontAwesomeIcon
                          icon={faStar}
                          className="mr-2 text-yellow-500 w-4 h-4"
                        />
                        {customExcerpt.reviewCount || "0"} Reviews
                      </li>
                      <li className="flex items-center">
                        <FontAwesomeIcon
                          icon={faMapMarkerAlt}
                          className="mr-2 text-[#005F59] w-4 h-4"
                        />
                        {customExcerpt.location || "Unknown"}
                      </li>
                    </ul>
                    <ul className="text-xs sm:text-sm text-gray-500 space-y-2">
                      <li className="flex items-center">
                        <FontAwesomeIcon
                          icon={faMoneyBill}
                          className="mr-2 text-green-500 w-4 h-4"
                        />
                        Fees: {customExcerpt.fees || "N/A"}
                      </li>
                      <li className="flex items-center">
                        <FontAwesomeIcon
                          icon={faChartLine}
                          className="mr-2 text-blue-500 w-4 h-4"
                        />
                        Cutoff: {customExcerpt.cutoff || "N/A"}
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 flex justify-between items-center">
                  <div className="">
                    <button
                      className="relative text-[#ff9245] px-3 py-1 !ring-1 ring-[#ff9245] rounded-full transition-colors hover:text-[#fff] hover:bg-[#ff9245] text-xs md:text-sm duration-300"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(post.slug);
                      }}
                    >
                      View Details
                    </button>
                  </div>
                  <div className="animate-blink rounded-full">
                    <button
                      className="px-3 py-1 text-xs md:text-sm rounded-full bg-[#ff9245] text-white border border-[#ff9245] hover:bg-white hover:!text-[#ff9245] hover:border-[#ff9245] hover:ring-1 hover:ring-[#ff9245] transition-colors duration-300 flex items-center"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleStartApplication(post);
                      }}
                    >
                      <FaPaperPlane className="mr-2" />
                      <span>Start Application</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {selectedCollege && (
        <CounsellingModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          college={selectedCollege}
          course={selectedCollege.course}
          location={selectedCollege.location}
        />
      )}
    </div>
  );
};

export default CollegeByLocation;
